import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Globe, Smartphone, Cpu, Check, Star, Sparkles, Zap, ShieldCheck, Code2,
} from "lucide-react";
import api, { API } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { getIcon } from "@/lib/icons";
import { Reveal, SectionHeader } from "@/components/Reveal";
import { PortfolioCard } from "@/components/PortfolioShowcase";
import Seo from "@/components/Seo";

const TECHS = ["Next.js", "React", "TypeScript", "Laravel", "PHP", "React Native", "Flutter", "Dart", "Kotlin", "Swift", "PostgreSQL", "MySQL", "Node.js", "Python", "Stripe", "AWS"];

export default function Home() {
  const { t, lang, pickL } = useI18n();
  const [data, setData] = useState({ services: [], packages: [], portfolio: [], testimonials: [], faqs: [], settings: {} });

  useEffect(() => {
    // Resilient load: one failing endpoint must never blank the whole page.
    Promise.allSettled([
      api.get("/services"), api.get("/packages"), api.get("/portfolio"),
      api.get("/testimonials"), api.get("/faqs"), api.get("/settings"),
    ]).then(([s, p, pf, tt, f, st]) => {
      const val = (r, fb) => (r.status === "fulfilled" ? r.value.data : fb);
      setData({
        services: val(s, []), packages: val(p, []), portfolio: val(pf, []),
        testimonials: val(tt, []), faqs: val(f, []), settings: val(st, {}),
      });
    });
  }, []);

  const { settings } = data;
  const heroTitle = pickL(settings, "hero_title") || t("footer.tagline");
  const heroSub = pickL(settings, "hero_subtitle") || t("hero.desc");
  const featured = data.services.filter((s) => s.featured).slice(0, 6);
  const stats = settings.stats || { projects: 120, clients: 85, years: 8, satisfaction: 98 };

  return (
    <div>
      <Seo title={pickL(settings, "seo_title") || undefined} description={pickL(settings, "seo_description") || undefined} />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-40 -right-32 h-[560px] w-[560px] rounded-full bg-primary/20 blur-[130px]" />
        <div className="absolute top-40 -left-40 h-[420px] w-[420px] rounded-full bg-primary/10 blur-[120px]" />
        <div className="container-x relative pt-20 pb-24 md:pt-28 md:pb-32 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <motion.span initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-4 py-1.5 text-xs font-semibold text-primary">
              <Sparkles size={14} /> {t("hero.badge")}
            </motion.span>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
              className="mt-6 font-display text-[2rem] sm:text-5xl md:text-6xl font-black tracking-tighter leading-[1.08] sm:leading-[1.05] break-words hyphens-auto"
              data-testid="hero-title">
              {heroTitle.split(".").filter(Boolean).map((line, i) => (
                <span key={i} className="block">{line.trim()}{i < heroTitle.split(".").filter(Boolean).length - 1 ? "." : ""}</span>
              ))}
              <span className="block text-primary mt-2">{heroSub}</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
              className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-xl">
              {t("hero.desc")}
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              className="mt-8 flex flex-wrap gap-3">
              <Link to="/quote" className="btn-primary" data-testid="hero-cta-quote">
                {t("hero.cta1")} <ArrowRight size={18} />
              </Link>
              <Link to="/portfolio" className="btn-ghost" data-testid="hero-cta-portfolio">{t("hero.cta2")}</Link>
            </motion.div>
            <div className="mt-10 flex flex-wrap gap-6 text-sm text-muted-foreground">
              {[[ShieldCheck, "GDPR-ready"], [Zap, "Fast & SEO"], [Globe, "DE / EN"]].map(([I, l], i) => (
                <span key={i} className="inline-flex items-center gap-2"><I size={16} className="text-primary" /> {l}</span>
              ))}
            </div>
          </div>
          <HeroVisual />
        </div>
        {/* tech marquee */}
        <div className="border-y border-border bg-card/50 py-5 overflow-hidden">
          <div className="flex gap-3 animate-[scroll_30s_linear_infinite] whitespace-nowrap" style={{ animation: "none" }}>
            <div className="container-x flex flex-wrap justify-center gap-2.5">
              {TECHS.map((tech) => (
                <span key={tech} className="rounded-full border border-border px-4 py-1.5 text-sm font-medium text-muted-foreground">{tech}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="container-x py-24">
        <Reveal><SectionHeader eyebrow={t("nav.services")} title={t("home.servicesTitle")} subtitle={t("home.servicesSub")} /></Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {(featured.length ? featured : data.services.slice(0, 6)).map((s, i) => (
            <Reveal key={s.id} delay={i * 0.05}>
              <Link to={`/services/${s.slug}`} data-testid={`home-service-${s.slug}`}
                className="group surface p-7 block h-full hover:border-primary transition-colors relative overflow-hidden">
                <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-primary/5 group-hover:bg-primary/10 transition-colors" />
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary mb-5">
                  {getIcon(s.icon, { size: 22 })}
                </div>
                <h3 className="font-display font-bold text-lg mb-2">{pickL(s, "name")}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{pickL(s, "short")}</p>
                <div className="mt-5 flex items-center justify-between">
                  <span className="text-sm font-semibold text-primary">{t("common.from")} {s.starting_price}€</span>
                  <ArrowRight size={16} className="text-primary opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link to="/services" className="btn-ghost" data-testid="home-all-services">{t("common.viewAll")} <ArrowRight size={16} /></Link>
        </div>
      </section>

      {/* THREE PILLARS */}
      <section className="container-x py-12 grid md:grid-cols-3 gap-6">
        {[
          [Globe, t("nav.web"), "/web-development", "web"],
          [Smartphone, t("nav.mobile"), "/mobile-development", "mobile"],
          [Cpu, t("nav.software"), "/business-software", "software"],
        ].map(([I, title, to], i) => (
          <Reveal key={to} delay={i * 0.08}>
            <Link to={to} className="group relative surface p-8 block h-full hover:glow-orange transition-all" data-testid={`pillar-${to.slice(1)}`}>
              <I size={30} className="text-primary mb-5" />
              <h3 className="font-display font-bold text-xl mb-2">{title}</h3>
              <p className="text-sm text-muted-foreground">
                {lang === "de" ? "Professionell entwickelt für Ihren Erfolg." : "Professionally built for your success."}
              </p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                {t("common.learnMore")} <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          </Reveal>
        ))}
      </section>

      {/* PROCESS */}
      <section className="container-x py-24">
        <Reveal><SectionHeader eyebrow={t("nav.process")} title={t("home.processTitle")} subtitle={t("home.processSub")} center /></Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            [lang === "de" ? "Beratung" : "Discovery", lang === "de" ? "Wir verstehen Ihre Ziele." : "We understand your goals."],
            [lang === "de" ? "Konzept & Design" : "Concept & Design", lang === "de" ? "UX/UI, das überzeugt." : "UX/UI that convinces."],
            [lang === "de" ? "Entwicklung" : "Development", lang === "de" ? "Sauberer, skalierbarer Code." : "Clean, scalable code."],
            [lang === "de" ? "Launch & Support" : "Launch & Support", lang === "de" ? "Go-live und Betreuung." : "Go-live and care."],
          ].map(([title, desc], i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div className="surface p-7 h-full">
                <span className="font-display text-4xl font-black text-primary/30">0{i + 1}</span>
                <h3 className="font-display font-bold text-lg mt-3 mb-2">{title}</h3>
                <p className="text-sm text-muted-foreground">{desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PORTFOLIO */}
      <section className="container-x py-12">
        <Reveal><SectionHeader eyebrow={t("nav.portfolio")} title={t("home.portfolioTitle")} subtitle={t("home.portfolioSub")} /></Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.portfolio.filter((p) => p.featured && p.active !== false).slice(0, 3).map((p, i) => (
            <Reveal key={p.id} delay={i * 0.06}>
              <PortfolioCard p={p} index={i} />
            </Reveal>
          ))}
        </div>
      </section>

      {/* PACKAGES PREVIEW */}
      <section className="container-x py-24">
        <Reveal><SectionHeader eyebrow={t("nav.packages")} title={t("home.packagesTitle")} subtitle={t("home.packagesSub")} center /></Reveal>
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {data.packages.filter((p) => p.type === "website").slice(0, 3).map((p, i) => (
            <Reveal key={p.id} delay={i * 0.06}>
              <div className={`surface p-8 h-full relative ${p.recommended ? "border-primary glow-orange" : ""}`} data-testid={`home-package-${i}`}>
                {p.popular && <span className="absolute -top-3 left-6 rounded-full bg-primary text-primary-foreground text-xs font-bold px-3 py-1">Popular</span>}
                {p.recommended && <span className="absolute -top-3 left-6 rounded-full bg-primary text-primary-foreground text-xs font-bold px-3 py-1">{lang === "de" ? "Empfohlen" : "Recommended"}</span>}
                <h3 className="font-display font-bold text-xl">{pickL(p, "name")}</h3>
                <div className="mt-3 mb-6"><span className="font-display text-4xl font-black">{p.price}€</span></div>
                <ul className="space-y-2.5">
                  {(pickL(p, "features") || []).slice(0, 5).map((f, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm"><Check size={16} className="text-primary shrink-0 mt-0.5" /> {f}</li>
                  ))}
                </ul>
                <Link to="/packages" className={`mt-6 w-full ${p.recommended ? "btn-primary" : "btn-ghost"}`}>{pickL(p, "cta") || t("common.getStarted")}</Link>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-border bg-card">
        <div className="container-x py-16 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[["projects", t("stats.projects")], ["clients", t("stats.clients")], ["years", t("stats.years")], ["satisfaction", t("stats.satisfaction")]].map(([k, l]) => (
            <Reveal key={k}>
              <div>
                <div className="font-display text-4xl md:text-5xl font-black text-primary">{stats[k]}{k === "satisfaction" ? "%" : "+"}</div>
                <div className="mt-2 text-sm text-muted-foreground">{l}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="container-x py-24">
        <Reveal><SectionHeader eyebrow="Testimonials" title={t("home.testimonialsTitle")} center /></Reveal>
        <div className="grid md:grid-cols-3 gap-6">
          {data.testimonials.map((tst, i) => (
            <Reveal key={tst.id} delay={i * 0.06}>
              <div className="surface p-7 h-full">
                <div className="flex gap-0.5 mb-4">{Array.from({ length: tst.rating || 5 }).map((_, j) => <Star key={j} size={16} className="fill-primary text-primary" />)}</div>
                <p className="text-sm leading-relaxed">"{pickL(tst, "text")}"</p>
                <div className="mt-5 flex items-center gap-3">
                  <img src={tst.image} alt={tst.name} className="h-11 w-11 rounded-full object-cover" />
                  <div><div className="font-semibold text-sm">{tst.name}</div><div className="text-xs text-muted-foreground">{pickL(tst, "role")}</div></div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="container-x py-12 max-w-3xl">
        <Reveal><SectionHeader eyebrow="FAQ" title={t("home.faqTitle")} center /></Reveal>
        <div className="space-y-3">
          {data.faqs.map((f) => <FaqItem key={f.id} q={pickL(f, "q")} a={pickL(f, "a")} />)}
        </div>
      </section>

      {/* CTA */}
      <section className="container-x pb-24">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl bg-primary p-12 md:p-16 text-center text-primary-foreground">
            <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 30% 30%, white, transparent 60%)" }} />
            <h2 className="relative font-display text-3xl md:text-4xl font-black tracking-tight">{t("home.ctaTitle")}</h2>
            <p className="relative mt-4 max-w-xl mx-auto opacity-90">{t("home.ctaDesc")}</p>
            <Link to="/quote" className="relative mt-8 inline-flex items-center gap-2 rounded-md bg-background text-foreground px-7 py-3.5 font-semibold hover:opacity-90 transition-opacity" data-testid="cta-quote">
              {t("hero.cta1")} <ArrowRight size={18} />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="surface overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-5 text-left font-medium" data-testid="faq-item">
        {q}
        <span className={`text-primary transition-transform ${open ? "rotate-45" : ""}`}>+</span>
      </button>
      {open && <div className="px-5 pb-5 text-sm text-muted-foreground -mt-1">{a}</div>}
    </div>
  );
}

function HeroVisual() {
  return (
    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2, duration: 0.6 }}
      className="relative hidden lg:block">
      <div className="surface p-6 glow-orange">
        <div className="flex gap-1.5 mb-4">
          {["#FF5A00", "#A39F99", "#2C2A26"].map((c) => <span key={c} className="h-3 w-3 rounded-full" style={{ background: c }} />)}
        </div>
        <pre className="text-xs leading-6 font-mono text-muted-foreground overflow-hidden">
{`import { build } from "jonuzovicdesign";

const project = build({
  web: "Next.js + React",
  mobile: ["iOS", "Android"],
  backend: "Laravel / FastAPI",
  design: "premium",
});

`}<span className="text-primary">{`export default project; // 🚀 launch`}</span>
        </pre>
      </div>
      <motion.div animate={{ y: [0, -12, 0] }} transition={{ duration: 4, repeat: Infinity }}
        className="absolute -bottom-8 -left-8 surface p-4 flex items-center gap-3 shadow-xl">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary"><Code2 size={20} /></div>
        <div><div className="text-xs text-muted-foreground">Clean Code</div><div className="font-semibold text-sm">100% Custom</div></div>
      </motion.div>
      <motion.div animate={{ y: [0, 12, 0] }} transition={{ duration: 5, repeat: Infinity }}
        className="absolute -top-6 -right-6 surface p-4 flex items-center gap-3 shadow-xl">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary"><Zap size={20} /></div>
        <div><div className="text-xs text-muted-foreground">Performance</div><div className="font-semibold text-sm">98 Score</div></div>
      </motion.div>
    </motion.div>
  );
}
